<script lang="ts">
    import {
        WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES,
        WISHLIST_SOCIAL_SHARE_ICONS,
        WISHLIST_SOCIAL_SHARE_SETTINGS,
    } from "@apps/wishlist/constants/constants";
    import { WishlistsStorefrontValuesSettingsEntity } from "@apps/wishlist/entities/WishlistsStorefrontValuesSettingsEntity";
    import CN from "classnames";
    import { createEventDispatcher } from "svelte";
    import { fade } from "svelte/transition";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Icon from "@components/Icon.svelte";
    import Text from "@components/Typography/Text.svelte";

    export let wishlistSocialShareChannels: WishlistsStorefrontValuesSettingsEntity["wishlistSocialShareChannels"];
    export let isShareLoading: boolean;
    export let label: string | undefined = undefined;
    export let isShareLinkCopied: boolean;
    export let linkCopiedLabel: string;
    export let iconsSize = "2.375em";

    const dispatch = createEventDispatcher();

    const handleShare = (channelName: WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES) => {
        dispatch("share", channelName);
    };
</script>

<div class="gw-wl-social-share">
    {#if label}
        <span class="gw-wl-social-share__title">
            {label}
        </span>
    {/if}
    <ul class="gw-wl-social-share__channels gw-list-reset-default-styles">
        {#each wishlistSocialShareChannels as { channelName }}
            <li>
                <BaseButton
                    class={CN(
                        "gw-wl-social-share__btn",
                        WISHLIST_SOCIAL_SHARE_SETTINGS[channelName].className
                    )}
                    on:click={() => handleShare(channelName)}
                    disabled={isShareLoading}
                    ariaLabel={channelName ===
                    WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES.copyShareLink
                        ? "copy share link"
                        : "share with " + channelName}
                >
                    <Icon
                        class="gw-wl-social-share__icon"
                        width={iconsSize}
                        height={iconsSize}
                    >
                        <svelte:component
                            this={WISHLIST_SOCIAL_SHARE_ICONS[channelName]}
                        />
                    </Icon>
                    {#if channelName === WISHLIST_SOCIAL_SHARE_CHANNEL_NAMES.copyShareLink && isShareLinkCopied}
                        <div
                            transition:fade={{ duration: 200 }}
                            class="gw-wl-social-share__icon-tooltip"
                        >
                            <Text component="span" variant="button" size="xs">
                                {linkCopiedLabel}
                            </Text>
                        </div>
                    {/if}
                </BaseButton>
            </li>
        {/each}
    </ul>
</div>

<style lang="postcss">
    .gw-wl-social-share__title {
        font-size: 0.875em;
        font-weight: 600;
    }

    .gw-wl-social-share {
        display: flex;
        align-items: center;
        gap: 0.5em;
        flex-wrap: wrap;

        :global(&__copy-link-btn:hover path) {
            fill: var(--gw-wl-link-hover);
        }

        :global(&__email-btn:hover path) {
            fill: var(--gw-wl-email-hover);
        }

        :global(&__facebook-btn:hover path) {
            fill: var(--gw-wl-facebook-hover);
        }

        :global(&__twitter-btn:hover path) {
            fill: var(--gw-wl-twitter-hover);
        }

        :global(&__pinterest-btn:hover path) {
            fill: var(--gw-wl-pinterest-hover);
        }

        :global(&__whatsapp-btn:hover path) {
            fill: var(--gw-wl-whatsapp-hover);
        }
    }

    .gw-wl-social-share__channels {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--gw-wl-share-channels-gap, 1.125em);
        padding: 0;
        margin: 0;
        list-style: none;

        @media screen and (max-width: 500px) {
            gap: 0.75em;
        }
    }

    :global(.gw-wl-social-share__btn) {
        display: flex;
        position: relative;
        font-size: 1em;
        &:disabled {
            opacity: 0.6;
        }
    }

    :global(.gw-wl-social-share__icon) {
        fill: transparent;
    }

    .gw-wl-social-share__icon-tooltip {
        position: absolute;
        top: calc(100% + 5px);
        background: var(--gw-wl-link-hover);
        left: 50%;
        width: fit-content;
        white-space: nowrap;
        transform: translateX(-50%);
        padding: var(--gw-space-1) var(--gw-space-2);
        color: #fff;
        border-radius: var(--gw-border-radius-sm);
    }
    .gw-wl-social-share__icon-tooltip::after {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 5px solid;
        border-color: transparent transparent var(--gw-wl-link-hover);
    }
</style>
